<script setup>
/**
 * NotificationItem - Individual notification row
 * Displays a single notification with icon, message, time, and read status
 */
import { computed } from 'vue';
import { notificationService } from '@/services/notificationService';

const props = defineProps({
  notification: {
    type: Object,
    required: true
  },
  showCompanyBadge: {
    type: Boolean,
    default: false
  },
  companyName: {
    type: String,
    default: null
  },
  hasCompanyAccess: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['click', 'toggle-read', 'delete']);

const icon = computed(() => {
  return notificationService.getNotificationIcon(props.notification.type);
});

const colorClass = computed(() => {
  return notificationService.getNotificationColor(props.notification.type);
});

const timeAgo = computed(() => {
  return notificationService.formatTimestamp(props.notification.createdAt);
});

const handleClick = () => {
  // Don't navigate for inaccessible notifications
  if (!props.hasCompanyAccess) {
    return;
  }
  emit('click', props.notification);
};

const handleToggleRead = (event) => {
  event.stopPropagation();
  emit('toggle-read', props.notification);
};

const handleDelete = (event) => {
  event.stopPropagation();
  emit('delete', props.notification);
};
</script>

<template>
  <div
    class="notification-item"
    :class="{
      'is-unread': !notification.isRead,
      'is-inaccessible': !hasCompanyAccess
    }"
    @click="handleClick"
  >
    <div class="notification-icon" :class="colorClass">
      <font-awesome-icon :icon="icon" />
    </div>
    <div class="notification-content">
      <div class="notification-header">
        <p class="notification-title">{{ notification.title }}</p>
        <span v-if="showCompanyBadge && companyName" class="company-badge">
          {{ companyName }}
        </span>
      </div>
      <p class="notification-message">{{ notification.message }}</p>
      <p class="notification-time">{{ timeAgo }}</p>
    </div>
    <div class="notification-actions">
      <button
        class="action-btn toggle-read-btn"
        :class="{ 'is-read': notification.isRead }"
        @click="handleToggleRead"
        :title="notification.isRead ? 'Mark as unread' : 'Mark as read'"
      >
        <font-awesome-icon :icon="notification.isRead ? ['fad', 'eye-low-vision'] : ['fad', 'eye']" />
      </button>
      <button
        v-if="notification.isRead"
        class="action-btn delete-btn"
        @click="handleDelete"
        title="Delete notification"
      >
        <font-awesome-icon :icon="['fad', 'trash']" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  position: relative;

  &:hover {
    background-color: var(--bulma-scheme-main-bis);
  }

  &.is-unread {
    background-color: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.05);
  }

  &.is-inaccessible {
    opacity: 0.5;
    cursor: not-allowed;

    .notification-title,
    .notification-message {
      text-decoration: line-through;
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--bulma-border-weak);
  }
}

.notification-icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--bulma-scheme-main-bis);
  font-size: 0.875rem;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.company-badge {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  background-color: var(--bulma-scheme-main-bis);
  color: var(--bulma-text-weak);
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
}

.notification-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--bulma-text-strong);
  margin-bottom: 0.125rem;
  line-height: 1.3;
}

.notification-message {
  font-size: 0.8125rem;
  color: var(--bulma-text);
  margin-bottom: 0.25rem;
  line-height: 1.4;

  // Truncate long messages
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-time {
  font-size: 0.75rem;
  color: var(--bulma-text-weak);
}

.notification-actions {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.action-btn {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.75rem;
}

.toggle-read-btn {
  color: var(--bulma-primary);

  &:hover {
    background-color: hsla(var(--bulma-primary-h), var(--bulma-primary-s), var(--bulma-primary-l), 0.15);
  }

  &.is-read {
    color: var(--bulma-success);

    &:hover {
      background-color: hsla(var(--bulma-success-h), var(--bulma-success-s), var(--bulma-success-l), 0.15);
    }
  }
}

.delete-btn {
  color: var(--bulma-danger);

  &:hover {
    background-color: hsla(var(--bulma-danger-h), var(--bulma-danger-s), var(--bulma-danger-l), 0.15);
  }
}
</style>
